import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useStringsContext } from '../../../../contexts/strings';
import { useNewsNotificationConf } from '../../../../hooks/useNews';
import { useSegments } from '../../../../hooks/useSegments';
import moment from 'moment';
import PushNotificationPopup, {
  PushNotificationFormPayload,
} from '../../../common/push-notification/push-notification-form';
import PushNotificationColumn from '../../../common/push-notification/push-notification-column';

interface Props {
  newsItem: any;
  selectedAbbrv: string;
  isLeague: boolean;
}

export default function NewsPushNotificationColumn(props: Props) {
  const { strings } = useStringsContext();
  const { selectedAbbrv, newsItem } = props;
  const [openPushNotificationPopup, setOpenPushNotificationPopup] = React.useState(false);
  const { query, createMutation, updateMutation, deleteMutation } = useNewsNotificationConf({
    abbrv: selectedAbbrv,
    id: newsItem.id,
  });
  const { query: segmentQuery } = useSegments(selectedAbbrv);
  const isLoading = query.isLoading || segmentQuery.isLoading;
  const newsNotificationConf = query.data;
  const pushNotificationId = newsNotificationConf?.id;

  if (isLoading) {
    return <>{strings.pushNotifications.loading}</>;
  }

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <PushNotificationColumn
        pushNotification={newsNotificationConf}
        onClick={() => {
          setOpenPushNotificationPopup(true);
        }}
        abbrv={selectedAbbrv}
      />

      {openPushNotificationPopup ? (
        <PushNotificationPopup
          abbrv={selectedAbbrv}
          isNews
          itemInfo={
            <Alert icon={false} severity="info" color="info" variant="standard">
              <Typography variant="h6">{newsItem.title}</Typography>
            </Alert>
          }
          content={{
            target: newsNotificationConf?.notification?.target,
            scheduledTime: newsNotificationConf?.scheduledTime
              ? moment(newsNotificationConf.scheduledTime).format('YYYY-MM-DD[T]HH:mm')
              : undefined,
            title: newsNotificationConf?.notification?.title || newsItem.title || '',
            description:
              newsNotificationConf?.notification?.description || newsItem.description || '',
          }}
          segments={segmentQuery.data || []}
          pushNotificationIsSend={newsNotificationConf?.status === 'SENT'}
          onCreate={async (payload: PushNotificationFormPayload) => {
            const { scheduledTime, target, title, description } = payload;
            const res = await createMutation.mutateAsync({
              scheduledTime,
              notification: {
                title,
                description,
                image: newsItem.image,
                articleUrl: newsItem.articleUrl,
                target,
              },
            });
            query.refetch();
            return res;
          }}
          onUpdate={(payload: PushNotificationFormPayload) => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const { scheduledTime, target, title, description } = payload;
            return updateMutation.mutateAsync({
              id: pushNotificationId,
              scheduledTime,
              notification: {
                title,
                description,
                image: newsItem.image,
                articleUrl: newsItem.articleUrl,
                target,
              },
              createdAt: (newsNotificationConf?.createdAt || moment().format()) as string,
            });
          }}
          onDelete={async () => {
            if (!pushNotificationId) {
              return Promise.resolve();
            }
            const res = await deleteMutation.mutateAsync({
              id: pushNotificationId,
              newsId: newsItem.id,
            });
            query.refetch();
            return res;
          }}
          onClose={() => {
            setOpenPushNotificationPopup(false);
          }}
          isPending={
            createMutation.isPending ||
            updateMutation.isPending ||
            deleteMutation.isPending ||
            query.isLoading
          }
          isNew={!pushNotificationId}
        />
      ) : null}
    </Box>
  );
}
